// Helpers for the superadmin legacy-import page (LegacyBundleImportForm.vue).
// The same chunking rules as the backend's scripts/legacy_import/chunk.js.

// Import order matters: history resolves patients, procedures and items by code.
export const IMPORT_KINDS = ['patients', 'procedures', 'items', 'history'];

// express.json() on the API accepts 100 KB; stay well under it.
export const MAX_BYTES = 80000;
export const MAX_RECORDS = 2000;

const encoder = new TextEncoder();
const byteLength = value => encoder.encode(JSON.stringify(value)).length;

/**
 * Split records so each request body stays under maxBytes.
 *
 * @param {Array} records
 * @param {{maxBytes: number, maxRecords: number, envelopeBytes: number}} limits
 *   envelopeBytes = size of the request body without its records
 * @returns {Array[]} Chunks in input order
 */
export function chunkByBytes(records, { maxBytes, maxRecords, envelopeBytes }) {
  const chunks = [];
  let current = [];
  let size = envelopeBytes + 2; // "[]"

  records.forEach((record, index) => {
    const recordBytes = byteLength(record);
    if (envelopeBytes + 2 + recordBytes > maxBytes) {
      throw new Error(`Record ${index} is ${recordBytes} bytes, more than one request can carry`);
    }
    const added = recordBytes + (current.length > 0 ? 1 : 0); // comma
    if (current.length >= maxRecords || size + added > maxBytes) {
      chunks.push(current);
      current = [];
      size = envelopeBytes + 2;
    }
    size += recordBytes + (current.length > 0 ? 1 : 0);
    current.push(record);
  });
  if (current.length > 0) chunks.push(current);

  return chunks;
}

/**
 * Chunks for one kind, sized for the request envelope the page sends.
 *
 * @param {Object} envelope - request body without records (records: [])
 * @param {Array} records
 * @returns {Array[]}
 */
export function chunksFor(envelope, records) {
  return chunkByBytes(records, {
    maxBytes: MAX_BYTES,
    maxRecords: MAX_RECORDS,
    envelopeBytes: byteLength({ ...envelope, records: [] })
  });
}

/**
 * Fold one chunk report into a running total: numbers add up, arrays join,
 * nested objects merge, booleans OR (so refused stays true once seen).
 *
 * @param {Object|null} total
 * @param {Object} next
 * @returns {Object}
 */
export function mergeReports(total, next) {
  if (!total) return JSON.parse(JSON.stringify(next));
  const merged = { ...total };
  Object.entries(next).forEach(([key, value]) => {
    if (typeof value === 'number') merged[key] = (merged[key] || 0) + value;
    else if (Array.isArray(value)) merged[key] = [...(merged[key] || []), ...value];
    else if (value && typeof value === 'object') merged[key] = mergeReports(merged[key] || {}, value);
    else if (typeof value === 'boolean') merged[key] = Boolean(merged[key]) || value;
    else merged[key] = value;
  });
  return merged;
}

/**
 * Check a parsed bundle has what the import needs.
 *
 * @param {*} bundle
 * @returns {string|null} Problem, or null when usable
 */
export function bundleProblem(bundle) {
  if (!bundle || typeof bundle !== 'object') return 'The file is not a bundle';
  const missing = IMPORT_KINDS.filter(kind => !Array.isArray(bundle[kind]));
  if (missing.length > 0) return `The bundle has no ${missing.join(', ')} list`;

  // The server only sees one chunk at a time, so repeats across chunks would be
  // skipped silently or refused halfway through: catch them before anything is sent.
  const repeats = [
    ['patient number', bundle.patients.map(p => p.legacyDn)],
    ['procedure code', bundle.procedures.map(p => p.code)],
    ['product code', bundle.items.map(i => i.code)],
    ['visit', bundle.history.map(v => `${v.legacyDn} ${v.visitDate}`)]
  ];
  for (const [label, values] of repeats) {
    const seen = new Set();
    // The server trims values; records with no value are left for the server to refuse.
    const normalised = values.map(value => String(value == null ? '' : value).trim()).filter(Boolean);
    const dup = normalised.find(value => (seen.has(value) ? true : (seen.add(value), false)));
    if (dup !== undefined) return `The bundle repeats a ${label}: ${dup}`;
  }

  // A record too big for one request would only fail after earlier types were written.
  const worstEnvelope = { clinicId: 99999999, branchId: 99999999, kind: 'procedures', dryRun: false, df: 100, roomId: 99999999 };
  try {
    IMPORT_KINDS.forEach(kind => chunksFor({ ...worstEnvelope, kind }, bundle[kind]));
  } catch (error) {
    return error.message;
  }
  return null;
}

/**
 * What blocks the import and what the operator should know, from the branch
 * status (GET /superadmin/legacy-import/status) and the bundle.
 *
 * @param {Object} status - { clinic, counts, rooms, procedureCodes }
 * @param {Object} bundle
 * @returns {{blockers: string[], warnings: string[], resuming: boolean}}
 */
export function preflight(status, bundle) {
  const blockers = [];
  const warnings = [];
  const { counts } = status;
  const resuming = counts.importedPatients > 0;

  if (status.clinic.type !== 'STANDALONE') {
    blockers.push(
      `This clinic is ${status.clinic.type || 'not a single-branch clinic'}. Only single-branch (STANDALONE) clinics are supported.`
    );
  }
  if (status.rooms.length === 0) {
    blockers.push('The branch has no room. Add one room first; imported visits are placed in it.');
  }
  if (counts.otherAppointments > 0 || counts.receipts > 0) {
    blockers.push(
      `The branch is already in use (${counts.otherAppointments} appointment(s), ${counts.receipts} receipt(s)). ` +
        'Import into a new, unused branch.'
    );
  }
  const registered = counts.patients - counts.importedPatients;
  if (registered > 0) {
    blockers.push(
      `The branch already has ${registered} patient(s) registered in DentCloud. ` +
        'Import into an empty branch, before the clinic registers anyone.'
    );
  }

  if (!resuming) {
    const existing = new Set(status.procedureCodes);
    const clashes = bundle.procedures.map(p => p.code).filter(code => existing.has(code));
    if (clashes.length > 0) {
      warnings.push(
        `${clashes.length} procedure code(s) already exist in this branch and will be kept as they are ` +
          `(history will link to them): ${clashes.slice(0, 20).join(', ')}${clashes.length > 20 ? ', …' : ''}`
      );
    }
  }

  return { blockers, warnings, resuming };
}

/**
 * The post-import comparison shown at the end: what the branch holds against
 * what the bundle carried.
 *
 * @param {Object} counts - status.counts
 * @param {Object} bundle
 * @returns {Array<{label: string, expected: number, actual: number, ok: boolean}>}
 */
export function compareCounts(counts, bundle) {
  const visitItems = bundle.history.reduce((n, visit) => n + visit.items.length, 0);
  return [
    { label: 'Patients', expected: bundle.patients.length, actual: counts.importedPatients },
    { label: 'Imported visits', expected: bundle.history.length, actual: counts.importedVisits },
    { label: 'Imported visit items', expected: visitItems, actual: counts.importedVisitItems }
  ].map(row => ({ ...row, ok: row.actual === row.expected }));
}

/**
 * True when the branch holds everything the bundle carried and every safety
 * check is 0, i.e. there is nothing left to import.
 *
 * @param {Object} status
 * @param {Object} bundle
 * @returns {boolean}
 */
export function importComplete(status, bundle) {
  return (
    compareCounts(status.counts, bundle).every(row => row.ok) &&
    Object.values(status.checks).every(value => value === 0)
  );
}
